import { Field, Input } from "@norican/react";
import { type ReactNode, useId, useRef, useState } from "react";

import { SubjectLookup } from "@/components/subject-lookup";
import { subText } from "@/components/table";
import { type DirectoryMatch, useDirectorySearch } from "@/lib/api";

/**
 * Type a name, pick the person, get their sign-in name.
 *
 * The field used to be free text and the failure it produced was not a typo but
 * a *plausible* entry: this tenant answers a search for "wood" with a VP, an IT
 * admin account and a Gmail guest, and their display names are nearly the same.
 * Typing cannot distinguish them; picking can.
 *
 * Three decisions worth keeping:
 *
 * * **Search by name, store the UPN.** HR knows people by name, the tenant
 *   knows them by sign-in name, and the gap between the two is where the wrong
 *   Anna gets offboarded. Every row shows the UPN and the job title, because
 *   that is what separates the duplicates.
 * * **Typing is still allowed.** Somebody who already has the UPN pastes it and
 *   the list gets out of the way (the hook stops searching once the text is a
 *   complete address). A picker that *forces* selection breaks the one case
 *   that is already unambiguous.
 * * **Keyboard first.** Arrow keys move, Enter picks, Escape closes — a
 *   combobox that only works with a mouse is one HR will type around.
 *
 * `SubjectLookup` still renders underneath and still resolves whatever is in
 * the field, so a pasted UPN gets the same confirmation as a picked one.
 */
export function PersonPicker({
  label,
  required,
  hint,
  placeholder,
  value,
  onChange,
  className,
  size,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  placeholder?: string;
  value: string;
  onChange: (upn: string) => void;
  /** Lets a dense form place the picker in its own flex row. */
  className?: string;
  size?: "sm" | "md";
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching } = useDirectorySearch(value, open);
  const matches = data?.matches ?? [];

  const choose = (match: DirectoryMatch) => {
    onChange(match.upn ?? "");
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!matches.length) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => {
        const next = event.key === "ArrowDown" ? index + 1 : index - 1;
        return (next + matches.length) % matches.length;
      });
      return;
    }
    const chosen = matches[active];
    if (event.key === "Enter" && open && chosen) {
      // Only swallow Enter while a list is actually showing: otherwise this
      // would eat the keystroke that submits the form.
      event.preventDefault();
      choose(chosen);
    }
  };

  return (
    <div className={className}>
      <Field label={label} required={required} hint={hint}>
        <Input
          ref={inputRef}
          size={size}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          // A click on a row would otherwise be lost to the blur that closes
          // the list first.
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
        />
      </Field>

      <div className="relative">
        {open && matches.length > 0 ? (
          <ul
            id={listId}
            role="listbox"
            aria-label={`People matching ${value}`}
            className="border-border bg-bg absolute z-20 mt-1 w-full max-w-2xl overflow-hidden rounded border shadow-lg"
          >
            {matches.map((match, index) => (
              <li key={match.upn ?? index}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(match)}
                  className={`block w-full px-3 py-2 text-left ${
                    index === active ? "bg-bg-subtle" : ""
                  }`}
                >
                  <span className="text-fg block text-sm">
                    {match.display_name}
                    {match.account_enabled === false ? (
                      <span className={`${subText} ml-2`}>disabled</span>
                    ) : null}
                    {(match.user_type ?? "").toLowerCase() === "guest" ? (
                      <span className={`${subText} ml-2`}>guest</span>
                    ) : null}
                  </span>
                  {/* The UPN is the line that separates the three Carl Woods. */}
                  <span className={`${subText} block`}>
                    {[match.upn, match.job_title, match.department].filter(Boolean).join(" · ")}
                  </span>
                </button>
              </li>
            ))}
            {data?.truncated ? (
              <li className={`${subText} border-border border-t px-3 py-2`}>
                More people match. Keep typing to narrow it down.
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      {isFetching && open ? <p className={`${subText} mt-1`}>Searching the directory…</p> : null}
      <SubjectLookup upn={value} />
    </div>
  );
}
